(function () {
  'use strict';

  const API_BASE = '/apps/tecdoc';
  const STORAGE_KEY = 'vehicle_fitment_vehicle';
  const CLIENT_ID_KEY = 'dfn_tecdoc_client_id';
  let turnstileScriptPromise = null;

  function getClientId() {
    try {
      const existing = localStorage.getItem(CLIENT_ID_KEY);
      if (existing) return existing;
      const created = window.crypto && typeof window.crypto.randomUUID === 'function'
        ? window.crypto.randomUUID()
        : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
      localStorage.setItem(CLIENT_ID_KEY, created);
      return created;
    } catch (_) {
      return 'anonymous';
    }
  }

  function requestTurnstileToken(siteKey) {
    if (!turnstileScriptPromise) {
      turnstileScriptPromise = new Promise((resolve, reject) => {
        if (window.turnstile) { resolve(window.turnstile); return; }
        const script = document.createElement('script');
        script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
        script.async = true;
        script.defer = true;
        script.onload = () => resolve(window.turnstile);
        script.onerror = () => reject(new Error('Verification could not be loaded.'));
        document.head.appendChild(script);
      });
    }
    return turnstileScriptPromise.then((turnstile) => new Promise((resolve, reject) => {
      const overlay = document.createElement('div');
      overlay.setAttribute('role', 'dialog'); overlay.setAttribute('aria-modal', 'true');
      overlay.style.cssText = 'position:fixed;inset:0;z-index:2147483000;display:grid;place-items:center;padding:20px;background:rgba(0,0,0,.5)';
      const panel = document.createElement('div');
      panel.style.cssText = 'width:min(100%,420px);padding:24px;border-radius:16px;background:#fff;color:#111;text-align:center';
      const title = document.createElement('strong'); title.textContent = 'Quick verification'; title.style.cssText = 'display:block;margin-bottom:8px;font-size:18px';
      const message = document.createElement('p'); message.textContent = 'Confirm you are human to continue the registration search.'; message.style.cssText = 'margin:0 0 16px';
      const widget = document.createElement('div'); widget.style.cssText = 'display:flex;justify-content:center;min-height:65px';
      const cancel = document.createElement('button'); cancel.type = 'button'; cancel.textContent = 'Cancel'; cancel.style.cssText = 'margin-top:14px;border:0;background:transparent;text-decoration:underline;cursor:pointer';
      panel.append(title, message, widget, cancel); overlay.appendChild(panel); document.body.appendChild(overlay);
      let widgetId = null;
      const cleanup = () => { if (widgetId !== null) turnstile.remove(widgetId); overlay.remove(); };
      cancel.onclick = () => { cleanup(); reject(new Error('Verification cancelled.')); };
      widgetId = turnstile.render(widget, {
        sitekey: siteKey, action: 'plate_lookup', theme: 'auto', size: 'flexible',
        callback(token) { cleanup(); resolve(token); },
        'error-callback'() { cleanup(); reject(new Error('Verification failed.')); }
      });
    }));
  }

  const $ = (root, selector) => root ? root.querySelector(selector) : null;
  const $$ = (root, selector) => root ? Array.from(root.querySelectorAll(selector)) : [];

  function esc(value) {
    return String(value || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function api(path, allowChallenge = true) {
    return fetch(API_BASE + path, { headers: { Accept: 'application/json' } })
      .then(async (response) => {
        const json = await response.json().catch(() => null);
        if (!response.ok && allowChallenge && json && json.code === 'turnstile_required' && json.turnstileSiteKey) {
          const token = await requestTurnstileToken(json.turnstileSiteKey);
          return api(`${path}${path.includes('?') ? '&' : '?'}turnstileToken=${encodeURIComponent(token)}`, false);
        }
        if (!response.ok || !json || json.success === false) {
          throw new Error((json && json.error) || `Request failed (${response.status})`);
        }
        return json.data || [];
      });
  }

  function fieldValue(item, names) {
    for (const name of names) {
      if (item && item[name] != null) return item[name];
    }
    return '';
  }

  function translateVehicleText(value) {
    return window.ArcherVehicleLabels && window.ArcherVehicleLabels.translateText
      ? window.ArcherVehicleLabels.translateText(value)
      : String(value || '');
  }

  function vehicleName(vehicle) {
    return [
      fieldValue(vehicle, ['makeName', 'MakeName']),
      translateVehicleText(fieldValue(vehicle, ['modelName', 'ModelName'])),
      translateVehicleText(fieldValue(vehicle, ['typeName', 'TypeName']))
    ].filter(Boolean).join(' ');
  }

  function vehicleMeta(vehicle) {
    return [
      fieldValue(vehicle, ['engineCode', 'EngineCode']),
      fieldValue(vehicle, ['powerHp', 'PowerHp']),
      [fieldValue(vehicle, ['yearFrom', 'YearFrom']), fieldValue(vehicle, ['yearTo', 'YearTo'])].filter(Boolean).join('-')
    ].filter(Boolean).join(' | ');
  }

  function vehicleId(vehicle) {
    return fieldValue(vehicle, ['vehicleId', 'VehicleId', 'id', 'Id']);
  }

  function vehicleImage(vehicle) {
    return fieldValue(vehicle, ['imageUrl', 'ImageUrl']);
  }

  function option(select, value, label) {
    const node = document.createElement('option');
    node.value = value == null ? '' : String(value);
    node.textContent = label || '';
    select.appendChild(node);
    return node;
  }

  function setStatus(root, text) {
    const status = $(root, '[data-vf-status]');
    if (status) status.textContent = text || '';
  }

  function setLoading(select, label) {
    select.innerHTML = '';
    option(select, '', label || 'Loading...');
    select.disabled = true;
  }

  function resetSelect(select, label) {
    select.innerHTML = '';
    option(select, '', label);
    select.disabled = true;
  }

  function hasOption(select, value) {
    const target = String(value == null ? '' : value);
    return Array.from(select.options).some((item) => item.value === target);
  }

  function saveVehicle(root, vehicle) {
    const config = root.dataset;
    const rawVehicleId = vehicleId(vehicle);
    const normalizedVehicleId = rawVehicleId === '' || rawVehicleId == null ? '' : Number(rawVehicleId);
    const payload = {
      ...vehicle,
      id: Number.isFinite(normalizedVehicleId) ? normalizedVehicleId : rawVehicleId,
      storeId: config.storeId || 'archer-diesel',
      tecDocProfile: config.tecDocProfile || 'uk',
      family: config.family || 'injectors',
      savedAt: Date.now()
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
    document.dispatchEvent(new CustomEvent('vehiclefitment:selected', { detail: payload }));
    return payload;
  }

  function clearVehicle() {
    localStorage.removeItem(STORAGE_KEY);
    document.dispatchEvent(new CustomEvent('vehiclefitment:cleared'));
  }

  function shouldStartFreshSelection() {
    try {
      return new URLSearchParams(window.location.search).get('changeVehicle') === '1';
    } catch (_) {
      return false;
    }
  }

  function removeFreshSelectionFlag() {
    try {
      const url = new URL(window.location.href);
      if (!url.searchParams.has('changeVehicle')) return;
      url.searchParams.delete('changeVehicle');
      window.history.replaceState({}, document.title, `${url.pathname}${url.search}${url.hash}`);
    } catch (_) { }
  }

  function renderSelected(root, vehicle) {
    const selected = $(root, '[data-vf-selected]');
    if (!selected) return;

    if (!vehicle) {
      selected.classList.remove('is-visible');
      selected.innerHTML = '';
      return;
    }

    const label = vehicleName(vehicle);
    const meta = vehicleMeta(vehicle);
    const image = vehicleImage(vehicle);
    const make = fieldValue(vehicle, ['makeName', 'MakeName']);

    selected.classList.add('is-visible');
    selected.innerHTML = `
      <span class="vehicle-fitment__selected-media">
        ${image ? `<img src="${esc(image)}" alt="${esc(label)}" loading="lazy">` : `<span aria-hidden="true">${esc(String(make || label).slice(0, 1))}</span>`}
      </span>
      <span class="vehicle-fitment__selected-copy">
        <strong>${esc(label)}</strong>
        ${meta ? `<small>${esc(meta)}</small>` : ''}
      </span>`;
  }

  function selectedVehicleFromSelect(select) {
    const option = select && select.selectedOptions && select.selectedOptions[0];
    if (!option || !option.dataset.vehicle) return null;
    try {
      return JSON.parse(option.dataset.vehicle);
    } catch (_) {
      return null;
    }
  }

  function buildResultsUrl(root, saved) {
    const target = root.dataset.resultsUrl || '/pages/vehicle-results';
    const url = new URL(target, window.location.origin);
    url.searchParams.set('vehicleId', String(saved.id || saved.vehicleId || ''));
    url.searchParams.set('storeId', saved.storeId || root.dataset.storeId || 'archer-diesel');
    url.searchParams.set('tecDocProfile', saved.tecDocProfile || root.dataset.tecDocProfile || 'uk');
    url.searchParams.set('family', saved.family || root.dataset.family || 'injectors');
    return `${url.pathname}${url.search}`;
  }

  function initMedia(root) {
    const media = $(root, '[data-vf-media]');
    if (!media || media.dataset.mediaReady === 'true') return;
    media.dataset.mediaReady = 'true';

    const slides = $$(media, '[data-vf-slide]');
    const dots = $$(media, '[data-vf-media-dot]');
    const previous = $(media, '[data-vf-media-previous]');
    const next = $(media, '[data-vf-media-next]');
    if (slides.length < 2) return;

    let active = 0;
    let timer = null;
    const reducedMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    function show(index) {
      active = (index + slides.length) % slides.length;
      slides.forEach((slide, itemIndex) => slide.classList.toggle('is-active', itemIndex === active));
      dots.forEach((dot, itemIndex) => {
        const isActive = itemIndex === active;
        dot.classList.toggle('is-active', isActive);
        dot.setAttribute('aria-pressed', String(isActive));
      });
    }

    function stopAutoPlay() {
      if (!timer) return;
      window.clearTimeout(timer);
      timer = null;
    }

    function startAutoPlay() {
      stopAutoPlay();
      if (reducedMotion || document.hidden || !root.isConnected) return;
      timer = window.setTimeout(() => {
        show(active + 1);
        startAutoPlay();
      }, 5000);
    }

    function showFromControl(index) {
      show(index);
      startAutoPlay();
    }

    if (previous) previous.addEventListener('click', () => showFromControl(active - 1));
    if (next) next.addEventListener('click', () => showFromControl(active + 1));
    dots.forEach((dot, index) => dot.addEventListener('click', () => showFromControl(index)));
    document.addEventListener('visibilitychange', startAutoPlay);
    startAutoPlay();
  }

  function initFinder(root) {
    if (root.dataset.vfReady === 'true') return;
    root.dataset.vfReady = 'true';

    const makeSelect = $(root, '[data-vf-make]');
    const modelSelect = $(root, '[data-vf-model]');
    const vehicleSelect = $(root, '[data-vf-vehicle]');
    const confirmButton = $(root, '[data-vf-confirm]');
    const clearButton = $(root, '[data-vf-clear]');
    const plateForm = $(root, '[data-vf-plate-form]');
    const plateInput = $(root, '[data-vf-plate]');
    const plateSubmit = $(root, '[data-vf-plate-submit]');
    const labels = {
      make: root.dataset.makeLabel || 'Select make',
      model: root.dataset.modelLabel || 'Select model',
      vehicle: root.dataset.vehicleLabel || 'Select vehicle',
      loading: root.dataset.loadingLabel || 'Loading...'
    };

    const storeId = root.dataset.storeId || 'archer-diesel';
    const tecDocProfile = root.dataset.tecDocProfile || 'uk';
    const family = root.dataset.family || 'injectors';
    const language = root.dataset.language || 'en';
    const plateCountry = root.dataset.plateCountry || 'GB';
    const startFreshSelection = shouldStartFreshSelection();
    const savedVehicle = startFreshSelection ? null : readSavedVehicle(storeId, tecDocProfile);
    let modelRequestId = 0;
    let vehicleRequestId = 0;
    initMedia(root);

    if (startFreshSelection) {
      clearVehicle();
      removeFreshSelectionFlag();
    }

    function query(extra) {
      const params = new URLSearchParams({
        storeId,
        tecDocProfile,
        lang: language,
        family,
        ...extra
      });
      return params.toString();
    }

    setLoading(makeSelect, labels.loading);
    resetSelect(modelSelect, labels.model);
    resetSelect(vehicleSelect, labels.vehicle);
    renderSelected(root, savedVehicle);

    api(`/makes?${query({})}`)
      .then((makes) => {
        makeSelect.innerHTML = '';
        option(makeSelect, '', labels.make);
        makes.forEach((make) => option(makeSelect, fieldValue(make, ['id', 'Id']), fieldValue(make, ['name', 'Name'])));
        makeSelect.disabled = false;
        setStatus(root, makes.length ? '' : 'No compatible makes are available yet.');

        const savedMakeId = fieldValue(savedVehicle, ['makeId', 'MakeId']);
        const savedModelId = fieldValue(savedVehicle, ['modelSeriesId', 'ModelSeriesId']);
        const savedVehicleId = vehicleId(savedVehicle);
        if (!savedMakeId || !savedModelId || !savedVehicleId || !hasOption(makeSelect, savedMakeId)) return;

        makeSelect.value = String(savedMakeId);
        const currentModelRequest = ++modelRequestId;
        setLoading(modelSelect, labels.loading);

        return api(`/models?${query({ makeId: savedMakeId })}`)
          .then((models) => {
            if (currentModelRequest !== modelRequestId) return null;
            modelSelect.innerHTML = '';
            option(modelSelect, '', labels.model);
            models.forEach((model) => option(modelSelect, fieldValue(model, ['id', 'Id']), translateVehicleText(fieldValue(model, ['name', 'Name']))));
            modelSelect.disabled = false;
            if (!hasOption(modelSelect, savedModelId)) return null;

            modelSelect.value = String(savedModelId);
            const currentVehicleRequest = ++vehicleRequestId;
            setLoading(vehicleSelect, labels.loading);
            return api(`/vehicles?${query({ makeId: savedMakeId, modelSeriesId: savedModelId })}`)
              .then((vehicles) => ({ vehicles, currentVehicleRequest }));
          })
          .then((result) => {
            if (!result || result.currentVehicleRequest !== vehicleRequestId) return;
            vehicleSelect.innerHTML = '';
            option(vehicleSelect, '', labels.vehicle);
            result.vehicles.forEach((vehicle) => {
              const label = [vehicleName(vehicle), vehicleMeta(vehicle)].filter(Boolean).join(' - ');
              const node = option(vehicleSelect, fieldValue(vehicle, ['id', 'Id']), label);
              node.dataset.vehicle = JSON.stringify(vehicle);
            });
            vehicleSelect.disabled = false;
            if (hasOption(vehicleSelect, savedVehicleId)) {
              vehicleSelect.value = String(savedVehicleId);
              confirmButton.disabled = false;
            }
          });
      })
      .catch((error) => setStatus(root, error.message));

    makeSelect.addEventListener('change', () => {
      const currentModelRequest = ++modelRequestId;
      vehicleRequestId += 1;
      resetSelect(vehicleSelect, labels.vehicle);
      confirmButton.disabled = true;
      const makeId = makeSelect.value;
      if (!makeId) {
        resetSelect(modelSelect, labels.model);
        return;
      }

      setLoading(modelSelect, labels.loading);
      api(`/models?${query({ makeId })}`)
        .then((models) => {
          if (currentModelRequest !== modelRequestId) return;
          modelSelect.innerHTML = '';
          option(modelSelect, '', labels.model);
          models.forEach((model) => option(modelSelect, fieldValue(model, ['id', 'Id']), translateVehicleText(fieldValue(model, ['name', 'Name']))));
          modelSelect.disabled = false;
          setStatus(root, models.length ? '' : 'No compatible models are available for this make.');
        })
        .catch((error) => {
          if (currentModelRequest === modelRequestId) setStatus(root, error.message);
        });
    });

    modelSelect.addEventListener('change', () => {
      const currentVehicleRequest = ++vehicleRequestId;
      confirmButton.disabled = true;
      const makeId = makeSelect.value;
      const modelSeriesId = modelSelect.value;
      if (!makeId || !modelSeriesId) {
        resetSelect(vehicleSelect, labels.vehicle);
        return;
      }

      setLoading(vehicleSelect, labels.loading);
      api(`/vehicles?${query({ makeId, modelSeriesId })}`)
        .then((vehicles) => {
          if (currentVehicleRequest !== vehicleRequestId) return;
          vehicleSelect.innerHTML = '';
          option(vehicleSelect, '', labels.vehicle);
          vehicles.forEach((vehicle) => {
            const label = [vehicleName(vehicle), vehicleMeta(vehicle)].filter(Boolean).join(' - ');
            const node = option(vehicleSelect, fieldValue(vehicle, ['id', 'Id']), label);
            node.dataset.vehicle = JSON.stringify(vehicle);
          });
          vehicleSelect.disabled = false;
          setStatus(root, vehicles.length ? '' : 'No compatible engines are available for this model.');
        })
        .catch((error) => {
          if (currentVehicleRequest === vehicleRequestId) setStatus(root, error.message);
        });
    });

    vehicleSelect.addEventListener('change', () => {
      confirmButton.disabled = !selectedVehicleFromSelect(vehicleSelect);
    });

    if (plateInput) {
      plateInput.addEventListener('input', () => {
        plateInput.value = plateInput.value.toUpperCase().replace(/[^A-Z0-9 -]/g, '');
      });
    }

    if (plateForm && plateInput && plateSubmit) {
      plateForm.addEventListener('submit', (event) => {
        event.preventDefault();
        const plate = plateInput.value.trim().toUpperCase();
        if (plate.length < 4) {
          setStatus(root, root.dataset.plateEmptyLabel || 'Enter a valid UK registration.');
          plateInput.focus();
          return;
        }

        plateSubmit.disabled = true;
        plateInput.disabled = true;
        setStatus(root, root.dataset.plateSearchingLabel || 'Looking up your registration...');

        api(`/plate?${query({
          plate,
          country: plateCountry,
          lang: language,
          storeId,
          tecDocProfile,
          family,
          clientId: getClientId()
        })}`)
          .then((vehicles) => {
            if (!vehicles.length) {
              setStatus(root, root.dataset.plateNotFoundLabel || 'We could not find a compatible vehicle for that registration. Please select it manually below.');
              return;
            }

            if (vehicles.length === 1) {
              const saved = saveVehicle(root, vehicles[0]);
              renderSelected(root, saved);
              setStatus(root, 'Vehicle found. Loading compatible products...');
              window.location.href = buildResultsUrl(root, saved);
              return;
            }

            vehicleSelect.innerHTML = '';
            option(vehicleSelect, '', labels.vehicle);
            vehicles.forEach((vehicle) => {
              const label = [vehicleName(vehicle), vehicleMeta(vehicle)].filter(Boolean).join(' - ');
              const node = option(vehicleSelect, vehicleId(vehicle), label);
              node.dataset.vehicle = JSON.stringify(vehicle);
            });
            vehicleSelect.disabled = false;
            confirmButton.disabled = true;
            setStatus(root, root.dataset.plateMultipleLabel || 'We found more than one match. Select the correct engine below.');
            vehicleSelect.focus();
          })
          .catch((error) => setStatus(root, error.message))
          .finally(() => {
            plateSubmit.disabled = false;
            plateInput.disabled = false;
          });
      });
    }

    confirmButton.addEventListener('click', () => {
      const vehicle = selectedVehicleFromSelect(vehicleSelect);
      if (!vehicle) return;
      const saved = saveVehicle(root, vehicle);
      renderSelected(root, saved);
      setStatus(root, 'Vehicle saved. Product compatibility will now be checked.');

      window.location.href = buildResultsUrl(root, saved);
    });

    clearButton.addEventListener('click', () => {
      modelRequestId += 1;
      vehicleRequestId += 1;
      clearVehicle();
      renderSelected(root, null);
      makeSelect.value = '';
      resetSelect(modelSelect, labels.model);
      resetSelect(vehicleSelect, labels.vehicle);
      confirmButton.disabled = true;
      if (plateInput) plateInput.value = '';
      setStatus(root, 'Vehicle selection cleared.');
    });

    document.addEventListener('vehiclefitment:cleared', () => {
      modelRequestId += 1;
      vehicleRequestId += 1;
      renderSelected(root, null);
      makeSelect.value = '';
      resetSelect(modelSelect, labels.model);
      resetSelect(vehicleSelect, labels.vehicle);
      confirmButton.disabled = true;
    });
  }

  function readSavedVehicle(storeId, tecDocProfile) {
    try {
      const vehicle = JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null');
      if (!vehicle) return null;
      if (vehicle.storeId !== storeId) return null;
      const savedProfile = vehicle.tecDocProfile || vehicle.profile;
      if (savedProfile !== tecDocProfile) return null;
      return vehicle;
    } catch (_) {
      return null;
    }
  }

  function boot() {
    $$(document, '[data-vehicle-finder]').forEach(initFinder);
  }

  document.addEventListener('DOMContentLoaded', boot);
  document.addEventListener('shopify:section:load', boot);
})();
